/*
 * <announcement-bar> — dismiss + optional message rotation.
 * Dismissal is remembered in localStorage, keyed by a hash of the message text,
 * so editing the copy re-shows the bar. Rotation shows one message at a time.
 *
 * Messages that change by themselves must be stoppable (WCAG 2.2.2): a pause
 * button appears whenever they rotate, and rotation also holds while the
 * pointer is over the bar or focus is inside it — a link that vanished from
 * under the keyboard would drop focus to the top of the page. Under
 * prefers-reduced-motion it starts paused; the visitor can start it.
 * Guarded so a repeat only defines once.
 */
if (!customElements.get('announcement-bar')) {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messagesEl = this.querySelector('[data-ab-messages]');
      this.messages = Array.from(this.querySelectorAll('[data-ab-messages] > .shopify-block'));
      this.closeBtn = this.querySelector('[data-ab-close]');
      this.toggleBtn = this.querySelector('[data-ab-toggle]');
      if (!this.messagesEl || this.messages.length === 0) return;

      /* The key comes from Liquid (data-ab-key) so the inline pre-paint script
         in the section and this module agree on it. The DOM hash is only a
         fallback for markup rendered without the attribute. */
      const key = this.dataset.abKey || this.hash(this.messagesEl.textContent.replace(/\s+/g, ' ').trim());
      this.storeKey = 'announcement:' + key;
      let dismissed = false;
      try {
        dismissed = localStorage.getItem(this.storeKey) === '1';
      } catch (e) {
        /* private mode / storage blocked — just show the bar */
      }
      if (dismissed) {
        // Normally the inline script got here first; this covers the case where
        // it was blocked (CSP) or the section was re-rendered by the editor.
        this.hidden = true;
        return;
      }

      this.messagesEl.setAttribute('data-ready', '');
      this.index = 0;
      this.setActive(0);

      this.canRotate = this.dataset.rotate === 'true' && this.messages.length > 1;
      this.speed = (parseFloat(this.dataset.rotateSpeed) || 5) * 1000;
      this.paused = reduce.matches; // the visitor's choice; reduced motion makes it for them until they press play
      this.hovered = false;
      this.focused = false;

      // Once per element: the theme editor moves nodes, which runs this again.
      if (!this.bound) {
        this.bound = true;
        this.onMotion = () => {
          if (reduce.matches) this.paused = true;
          this.sync();
        };
        if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.dismiss());
        if (this.toggleBtn) {
          this.toggleBtn.addEventListener('click', () => {
            this.paused = !this.paused;
            this.sync();
          });
        }
        this.addEventListener('pointerenter', () => {
          this.hovered = true;
          this.sync();
        });
        this.addEventListener('pointerleave', () => {
          this.hovered = false;
          this.sync();
        });
        this.addEventListener('focusin', () => {
          this.focused = true;
          this.sync();
        });
        this.addEventListener('focusout', (event) => {
          if (this.contains(event.relatedTarget)) return;
          this.focused = false;
          this.sync();
        });
      }
      reduce.addEventListener('change', this.onMotion);
      if (this.toggleBtn) this.toggleBtn.hidden = !this.canRotate;
      this.sync();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.timer = null;
      if (this.onMotion) reduce.removeEventListener('change', this.onMotion);
    }

    /* One place decides whether the messages are moving. */
    sync() {
      clearInterval(this.timer);
      this.timer = null;
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-pressed', String(this.paused));
      if (!this.canRotate || this.hidden || this.paused || this.hovered || this.focused) return;
      this.timer = setInterval(() => this.setActive((this.index + 1) % this.messages.length), this.speed);
    }

    setActive(i) {
      this.index = i;
      this.messages.forEach((message, mi) => message.classList.toggle('is-active', mi === i));
    }

    dismiss() {
      // The button being pressed is about to disappear: hand focus on to the
      // header rather than let it fall back to the top of the document.
      if (this.contains(document.activeElement)) {
        const next = document.querySelector('[data-store-header] :is(button, a[href])') || document.getElementById('main-content');
        if (next) next.focus();
      }
      this.hidden = true;
      clearInterval(this.timer);
      this.timer = null;
      try {
        localStorage.setItem(this.storeKey, '1');
      } catch (e) {
        /* storage blocked — dismissal just won't persist */
      }
    }

    hash(str) {
      let h = 0;
      for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) | 0;
      return String(h >>> 0);
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
